import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  Tabs,
  Tag,
  TagCloseButton,
  TagLabel,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import { Fragment, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  CaretDown,
  CaretRight,
  ChartDonut,
  ChartLine,
  FlowArrow,
  ListBullets,
} from "@phosphor-icons/react";
import { DimensionDropdown, QuarterDropdown, YearDropdown } from "../FootPrint";
import YoYChip from "../YoYChip";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveLine } from "@nivo/line";
import { ResponsiveSankey } from "@nivo/sankey";
import { getFormattedNumValue } from "../../../../../utils";
import { toSafariSafeSankey } from "../../../../../utils/sankey";
import {
  indexScopeOneCategories,
  scopeOneCategoryRows,
  scopeOneDimensionRowsForFuel,
  scopeOneFuelRows,
  scopeOneQuarterlyByCategory,
  scopeOneQuarterlyCategoryRows,
} from "../../../../../utils/scopeOneDimensions";
import { useBreakdownLevel } from "../../../../../hooks/useBreakdownLevel";
import {
  BY_DIMENSION_PAGE_SIZE,
  QUARTERLY_BY_DIMENSION_PAGE_SIZE,
  dimensionFilterParams,
} from "../../../../../utils/breakdownLevel";
import { useGetCompanyDimensionReports, useGetQuarterlyCompanyDimensionReports } from "../../../../../hooks/query/reports";
import {
  useGetMultiYearEmissions,
  useGetQuarterlyEmissions,
  useGetQuarterlyMultiYearEmissions,
} from "../../../../../hooks/query/emissions-insight/emissions-insights-query";
import { axisBaselineLayer, chartTheme, rightLegend } from "../Consumption/chartTheme";
import SourceDataTasks from "./SourceDataTasks";
import QuartersSwitch, { quarterPeriodLabels } from "./QuartersSwitch";
import QuarterlyBreakdownTable, { useQuarterlyTableCompare } from "./QuarterlyBreakdownTable";
import JumpToScope from "./JumpToScope";
import ExpandCollapseControl from "./ExpandCollapseControl";


const STATIONARY_ACCENT = "#F3B85E";
const MOBILE_ACCENT = "#B45309";
const REFRIGERANT_ACCENT = "#5EB5F3";

const CATEGORY_META = {
  stationary:   { label: "Stationary",   accent: STATIONARY_ACCENT  },
  mobile:       { label: "Mobile",       accent: MOBILE_ACCENT      },
  refrigerants: { label: "Refrigerants", accent: REFRIGERANT_ACCENT },
};

const formatTco2 = (value) => {
  if (value == null) return null;
  return value.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatNumber = (value, digits = 0) => {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return Number(value).toLocaleString("en-GB", { maximumFractionDigits: digits });
};

const sumConsumption = (rows) =>
  (rows || []).reduce((acc, r) => acc + (Number(r.consumption) || 0), 0);

const sumEmissions = (rows) =>
  (rows || []).reduce((acc, r) => acc + (Number(r.emissions) || 0), 0);

// Sum the real kWh-equivalent across rows. Unlike raw consumption (which mixes units), the
// backend-derived kwh normalises to a single unit, so it is safe to total. Returns null when no
// row carries a kwh value (e.g. no calorific data configured yet), so callers can hide the field
// rather than show a misleading zero.
const sumKwh = (rows) => {
  const present = (rows || []).filter((r) => r.kwh != null && Number.isFinite(Number(r.kwh)));
  if (!present.length) return null;
  return present.reduce((acc, r) => acc + Number(r.kwh), 0);
};

// Pick a single dominant unit per category so the caption can read e.g.
// "3,057,239 kWh-equiv" without having to mix units. Falls back to "units"
// if there isn't a clear single unit.
const dominantUnit = (rows) => {
  if (!rows || !rows.length) return "units";
  const totals = new Map();
  rows.forEach((r) => {
    const u = r.measurementUnit || "units";
    totals.set(u, (totals.get(u) || 0) + (Number(r.consumption) || 0));
  });
  let topUnit = "units";
  let topVal = -Infinity;
  totals.forEach((v, k) => {
    if (v > topVal) { topUnit = k; topVal = v; }
  });
  return topUnit;
};

// Replace underscores/dashes with spaces and coerce to sentence case so
// backend enums like ELECTRICITY_MARKET_BASED render as "Electricity market
// based". Non-string inputs pass through unchanged.
const prettyLabel = (value) => {
  if (typeof value !== "string" || !value) return value;
  const lower = value.replace(/[_-]+/g, " ").trim().toLowerCase();
  if (!lower) return value;
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const ScopeOneDetail = ({
  currentYearData,
  previousYearData,
  isFetchingCurrentYear,
  isFetchingPreviousYear,
  year,
  setYear,
  quarter,
  setQuarter,
  dimension,
  setDimension,
  dimensionParams,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const handleBack = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("detailView");
    setSearchParams(params);
  };

  const stationaryRows = currentYearData?.scopeOneAndTwoDetail?.stationary || [];
  const mobileRows = currentYearData?.scopeOneAndTwoDetail?.mobile || [];
  const refrigerantRows = currentYearData?.scopeOneAndTwoDetail?.refrigerant || [];
  const prevStationaryRows = previousYearData?.scopeOneAndTwoDetail?.stationary || [];
  const prevMobileRows = previousYearData?.scopeOneAndTwoDetail?.mobile || [];
  const prevRefrigerantRows = previousYearData?.scopeOneAndTwoDetail?.refrigerant || [];

  const stationaryTco2 = sumEmissions(stationaryRows);
  const mobileTco2 = sumEmissions(mobileRows);
  const refrigerantTco2 = sumEmissions(refrigerantRows);
  const prevStationaryTco2 = sumEmissions(prevStationaryRows);
  const prevMobileTco2 = sumEmissions(prevMobileRows);
  const prevRefrigerantTco2 = sumEmissions(prevRefrigerantRows);

  const stationaryConsumption = sumConsumption(stationaryRows);
  const mobileConsumption = sumConsumption(mobileRows);

  // Real energy-equivalent totals (null until calorific values are configured), shown on the
  // Stationary/Mobile KPI cards. Refrigerants have no kWh-equivalent.
  const stationaryKwh = sumKwh(stationaryRows);
  const mobileKwh = sumKwh(mobileRows);

  const stationaryUnit = dominantUnit(stationaryRows);
  const mobileUnit = dominantUnit(mobileRows);
  const hasRefrigerants = refrigerantRows.length > 0;

  // null = whole year; the dimension-items report endpoint requires a
  // non-null quarter ("ALL").
  const reportQuarter = quarter || "ALL";

  // ESG-3960: the level the By-dimension table breaks down into — auto (the level below the deepest
  // filter) unless the user picks one from the dropdown. Shared with the other three drill-downs.
  const {
    organizationStructure,
    autoLevelKey,
    levelKey: nextLevelKey,
    levelDimension: nextLevelDimension,
    levelName: dimensionLevelName,
    levelOptions,
    onSelectLevel,
    hideByDimension,
  } = useBreakdownLevel(dimension);

  // Real per-dimension Scope 1 totals for the resolved level. Each row carries the dimension item's id
  // and name plus its scope totals and measured per-category split.
  //
  // ESG-3960: includeUnattributed asks for one extra row carrying the emissions that carry NO dimension
  // item at this level. Without it the table silently omits them and differs from the KPI card — on
  // staging that is 80% of Scope 3.
  const { data: dimensionReport, isFetching: isFetchingDimension } = useGetCompanyDimensionReports(
    nextLevelDimension?.id,
    {
      year,
      quarter: reportQuarter,
      page: 0,
      size: BY_DIMENSION_PAGE_SIZE,
      includeUnattributed: true,
      ...dimensionFilterParams(dimension),
    },
    { enabled: !!nextLevelDimension?.id && !!year }
  );

  // ESG-3960: a client-side guard used to live here, re-filtering the response by display NAME because
  // the backend's filtered recompute overwrote the user's own selection at the breakdown level with each
  // candidate in turn. The backend now intersects instead, so the guard is gone — which also removes a
  // name match that was wrong whenever two items at one level share a name.
  const dimensionRows = useMemo(() => {
    return (dimensionReport?.content || [])
      .filter((row) => !row.unattributed && row.dimensionItemId != null)
      .map((row) => ({
        id: row.dimensionItemId,
        name: row.dimensionItem,
        value: Number(row.scopeOneTotalCo2Emissions) || 0,
      }))
      .filter((r) => r.name && r.value > 0)
      .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));
  }, [dimensionReport]);

  // The "no {level} assigned" figure, or null when every row is tagged at this level.
  const unattributedScopeOne = useMemo(() => {
    const footer = (dimensionReport?.content || []).find((row) => row.unattributed);
    const value = Number(footer?.scopeOneTotalCo2Emissions) || 0;
    return value > 0 ? value : null;
  }, [dimensionReport]);

  // How many items exist at this level versus how many are shown, so the table can say so rather than
  // truncating in silence. The staging org's level-four dimension has 1,807 items.
  const dimensionTotalCount = dimensionReport?.totalElements ?? null;

  // ESG-3959: the MEASURED per-dimension Scope 1 split. ESG-3960: indexed by dimension item ID, not
  // name — a name-keyed index collapsed same-name twins onto whichever the query returned last, which
  // (ordering by emissions desc) was the one with the LOWER total.
  const scopeOneCategoryIndex = useMemo(
    () => indexScopeOneCategories(dimensionReport?.content),
    [dimensionReport]
  );

  // ---- Quarterly by-dimension view -------------------------------------
  // Toggle lives on the page so its queries only fire when the view is on.
  // Period index 0..3 = Q1..Q4, 4 = whole-year ("ALL"). ONE fan-out: the per-quarter dimension reports,
  // which since ESG-3959 carry each item's measured per-category and per-fuel split. There used to be a
  // second fan-out to /insights/total per quarter, needed only because the dimension report had no
  // per-category/kWh split and the table scaled the org-wide category totals by each item's share. That
  // is gone, and with it five requests per toggle. All four quarters are always shown regardless of the
  // page quarter filter — the quarters are the columns.
  //
  // ESG-3960: this fan-out asks for a SMALLER page than the annual view. Each of its five requests
  // recomputes one full report per item on the filtered path, so the cost is multiplied by five — and
  // the quarterly table is unreadable well before 200 rows anyway.
  const [dimQuarterly, setDimQuarterly] = useState(false);
  const quarterlyDimReports = useGetQuarterlyCompanyDimensionReports(
    nextLevelDimension?.id,
    { year, page: 0, size: QUARTERLY_BY_DIMENSION_PAGE_SIZE, ...dimensionFilterParams(dimension) },
    { enabled: !!nextLevelDimension?.id && !!year && dimQuarterly, retry: false, cacheTime: 60_000 }
  );

  // ESG-3959: the per-quarter by-dimension payloads, straight through. Each carries its own measured
  // scopeOneCategories per item, so there is nothing to apportion — the five period indexes are
  // [Q1..Q4, whole-year].
  const quarterlyDimContents = useMemo(
    () => quarterlyDimReports.map((q) => q.data?.content || []),
    [quarterlyDimReports]
  );

  // Item order and membership for the quarterly table: ordered by whole-year Scope 1 desc. Kept separate
  // from the figures so the util stays a pure transform. Keyed by id (ESG-3960).
  const quarterlyItems = useMemo(() => {
    if (!dimQuarterly) return [];
    const byId = new Map();
    quarterlyDimContents.forEach((content, pi) => {
      content.forEach((row) => {
        if (row.unattributed || row.dimensionItemId == null) return;
        const key = String(row.dimensionItemId);
        if (!byId.has(key)) {
          byId.set(key, { id: row.dimensionItemId, name: row.dimensionItem, periods: [0, 0, 0, 0, 0] });
        }
        byId.get(key).periods[pi] = Number(row.scopeOneTotalCo2Emissions) || 0;
      });
    });
    return [...byId.values()]
      .filter((item) => item.periods.some((v) => v > 0))
      .sort((a, b) => b.periods[4] - a.periods[4] || (a.name || "").localeCompare(b.name || ""))
      .map(({ id, name }) => ({ id, name }));
  }, [dimQuarterly, quarterlyDimContents]);

  const isFetchingQuarterlyDim = dimQuarterly && quarterlyDimReports.some((q) => q.isFetching);

  // Real 5-year per-category trend from /insights/total (the same endpoint the
  // KPI cards use — its payload carries scopeOneAndTwoDetail per category).
  // Current + previous are cache hits off FootPrint's queryKey, so pass RAW
  // quarter and the same dimensionParams string. Years with no report 404 and
  // are omitted (no fabricated fills). Mirrors ScopeTwoDetail.
  const trendYears = useMemo(() => [year - 4, year - 3, year - 2, year - 1, year], [year]);
  const trendQueries = useGetMultiYearEmissions(
    trendYears,
    dimensionParams,
    {
      onError: (error) => {
        if (error?.response?.status === 404) return;
      },
      retry: false,
      cacheTime: 60,
      enabled: !!year,
    },
    quarter
  );
  // Quarterly trend mode: one point per quarter instead of per year. Queries
  // only fire while the switch is on; the page's quarter filter is ignored
  // (all four quarters are always plotted). Mirrors EmissionsTrends.jsx.
  const [trendQuarters, setTrendQuarters] = useState(false);
  const quarterlyTrendQueries = useGetQuarterlyMultiYearEmissions(trendYears, dimensionParams, {
    enabled: !!year && trendQuarters,
    onError: (error) => {
      if (error?.response?.status === 404) return;
    },
    retry: false,
    cacheTime: 60,
  });

  const trendSeries = useMemo(() => {
    const SERIES_META = [
      { key: "stationary", id: "Stationary", color: STATIONARY_ACCENT },
      { key: "mobile", id: "Mobile", color: MOBILE_ACCENT },
      { key: "refrigerant", id: "Refrigerants", color: REFRIGERANT_ACCENT },
    ];
    // Period labels + payload lookup switch between annual and quarterly.
    const byPeriod = {};
    let periods;
    if (trendQuarters) {
      periods = quarterPeriodLabels(trendYears);
      quarterlyTrendQueries.forEach((q, i) => {
        if (q.data) byPeriod[periods[i]] = q.data;
      });
    } else {
      periods = trendYears.map(String);
      trendQueries.forEach((q, i) => {
        if (q.data) byPeriod[String(trendYears[i])] = q.data;
      });
    }
    return SERIES_META
      .map(({ key, id, color }) => ({
        id,
        color,
        data: periods
          .map((p) => {
            const d = byPeriod[p];
            if (!d) return null;
            const v = sumEmissions(d?.scopeOneAndTwoDetail?.[key]);
            return typeof v === "number" ? { x: p, y: v } : null;
          })
          .filter(Boolean),
      }))
      .filter((s) => s.data.length > 0);
  }, [trendQueries, trendYears, trendQuarters, quarterlyTrendQueries]);

  return (
    <Flex direction="column" gap={6}>
      <Flex justifyContent="space-between" alignItems="center" gap={3} flexWrap="wrap">
        <Button leftIcon={<ArrowLeft size={18} />} variant="plain" colorScheme="gray" onClick={handleBack}>
          Footprint
        </Button>
        <JumpToScope current="scopeOne" />
      </Flex>

      <Flex justify="space-between" align="flex-start" gap={4} flexWrap="wrap">
        <Box>
          <Text fontSize="11px" fontWeight="700" letterSpacing="0.06em" color="limeShade.600" textTransform="uppercase" mb={1}>
            Combustion
          </Text>
          <Text fontSize="40px" fontWeight="700" color="darkTeal" lineHeight="1.1">
            Scope 1 - Direct Emissions
          </Text>
        </Box>
        <Flex gap={2} mt={2}>
          {setDimension && <DimensionDropdown selected={dimension || {}} setSelected={setDimension} />}
          {setYear && <YearDropdown setYear={setYear} selectedYear={year} />}
          {setQuarter && <QuarterDropdown setQuarter={setQuarter} selectedQuarter={quarter} />}
        </Flex>
      </Flex>

      <SimpleGrid minChildWidth="300px" spacing={6} mt={6}>
        <KpiCard
          accent="pureBlack"
          title="Total"
          subtitle="All direct combustion"
          tco2e={currentYearData?.scope1Total}
          previousTco2e={previousYearData?.scope1Total}
          footerNote={
            currentYearData?.scope1ManualTotal > 0
              ? `Includes ${formatTco2(currentYearData.scope1ManualTotal)} tCO₂e manual entries`
              : undefined
          }
          loading={isFetchingCurrentYear || isFetchingPreviousYear}
        />
        <KpiCard
          accent={STATIONARY_ACCENT}
          title="Stationary"
          subtitle="Boilers, gensets, heating"
          tco2e={stationaryTco2}
          previousTco2e={prevStationaryTco2}
          footerKwh={stationaryKwh}
          footerTrailing={stationaryKwh != null ? "kWh-equiv" : undefined}
          loading={isFetchingCurrentYear || isFetchingPreviousYear}
        />
        <KpiCard
          accent={MOBILE_ACCENT}
          title="Mobile"
          subtitle="Fleet & equipment fuels"
          tco2e={mobileTco2}
          previousTco2e={prevMobileTco2}
          footerKwh={mobileKwh}
          footerTrailing={mobileKwh != null ? "kWh-equiv" : undefined}
          loading={isFetchingCurrentYear || isFetchingPreviousYear}
        />
        {hasRefrigerants && (
          <KpiCard
            accent={REFRIGERANT_ACCENT}
            title="Refrigerants"
            subtitle="Fugitive emissions"
            tco2e={refrigerantTco2}
            previousTco2e={prevRefrigerantTco2}
            loading={isFetchingCurrentYear || isFetchingPreviousYear}
          />
        )}
      </SimpleGrid>

      <Box mt={6}>
        <ScopeOneVisualisation
          trendSeries={trendSeries}
          stationaryRows={stationaryRows}
          mobileRows={mobileRows}
          refrigerantRows={refrigerantRows}
          trendQuarters={trendQuarters}
          setTrendQuarters={setTrendQuarters}
          year={year}
          dimensionParams={dimensionParams}
        />
      </Box>

      <Box mt={6}>
        <CategoryTabs
          hideByDimension={hideByDimension}
          dimensionRows={dimensionRows}
          dimensionLevelName={dimensionLevelName}
          unattributed={unattributedScopeOne}
          shownCount={dimensionRows.length}
          totalCount={dimensionTotalCount}
          isFetchingDimension={isFetchingDimension}
          levelOptions={levelOptions}
          selectedLevelKey={nextLevelKey}
          autoLevelKey={autoLevelKey}
          onSelectLevel={onSelectLevel}
          dimQuarterly={dimQuarterly}
          setDimQuarterly={setDimQuarterly}
          scopeOneCategoryIndex={scopeOneCategoryIndex}
          quarterlyDimContents={quarterlyDimContents}
          quarterlyItems={quarterlyItems}
          isFetchingQuarterlyDim={isFetchingQuarterlyDim}
          year={year}
          categories={[
            stationaryRows.length > 0 && {
              key: "stationary",
              consumption: stationaryConsumption,
              previousConsumption: sumConsumption(prevStationaryRows),
              kwh: stationaryKwh,
              emissions: stationaryTco2,
              previousEmissions: prevStationaryTco2,
              unit: stationaryUnit,
              currentRows: stationaryRows,
              previousRows: prevStationaryRows,
            },
            mobileRows.length > 0 && {
              key: "mobile",
              consumption: mobileConsumption,
              previousConsumption: sumConsumption(prevMobileRows),
              kwh: mobileKwh,
              emissions: mobileTco2,
              previousEmissions: prevMobileTco2,
              unit: mobileUnit,
              currentRows: mobileRows,
              previousRows: prevMobileRows,
            },
            refrigerantRows.length > 0 && {
              key: "refrigerants",
              consumption: sumConsumption(refrigerantRows),
              previousConsumption: sumConsumption(prevRefrigerantRows),
              kwh: sumKwh(refrigerantRows),
              emissions: sumEmissions(refrigerantRows),
              previousEmissions: sumEmissions(prevRefrigerantRows),
              unit: dominantUnit(refrigerantRows),
              currentRows: refrigerantRows,
              previousRows: prevRefrigerantRows,
            },
          ].filter(Boolean)}
        />
      </Box>

      <SourceDataTasks
        scope="SCOPE_1"
        year={year}
        quarter={quarter}
        dimension={dimension}
        organizationStructure={organizationStructure}
      />
    </Flex>
  );
};

// --- 5-year trend + emissions flow (mirrors the Scope 2/3 detail pages) ----

const TOTAL_ACCENT = "#0B5E30";

// 5-year line chart of Scope 1 emissions, one line per category (Stationary /
// Mobile / Refrigerants). Real per-year totals; years without data are filled
// by the parent's builder so the lines stay continuous.
const ScopeOneTrendChart = ({ trendSeries, showQuarters = false }) => {
  if (!trendSeries || trendSeries.length === 0) {
    // Quarterly mode can legitimately come up empty (data reported at the
    // yearly level only) — explain rather than vanish, so the switch
    // doesn't look broken.
    if (showQuarters) {
      return (
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
          <Text textAlign="center" color="gray.500" fontSize="13px">
            No quarterly Scope 1 data — emissions for these years were reported at the yearly level only.
          </Text>
        </Box>
      );
    }
    return null;
  }
  return (
    <Box>
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} h="360px">
        <ResponsiveLine
          data={trendSeries.map(({ id, data, color }) => ({ id, data, color }))}
          margin={{ top: 12, right: 24, bottom: showQuarters ? 76 : 56, left: 64 }}
          xScale={{ type: "point" }}
          yScale={{ type: "linear", min: 0, max: "auto", stacked: false }}
          colors={({ color }) => color}
          curve="monotoneX"
          enableArea
          areaOpacity={0.12}
          // Render lines + points via custom layers (not Nivo's built-in animated
          // points). The built-in point animation desyncs from the line path when
          // the data/x-domain churns on year switching. Mirrors ScopeTwoTrendChart.
          enablePoints={false}
          layers={[
            "grid",
            "markers",
            "axes",
            axisBaselineLayer,
            "areas",
            "crosshair",
            ({ series, lineGenerator, xScale, yScale }) => (
              <g>
                {series.map((s) => {
                  const path = lineGenerator(
                    s.data.map((d) => ({ x: xScale(d.data.x), y: yScale(d.data.y) }))
                  );
                  return (
                    <path
                      key={s.id}
                      d={path}
                      fill="none"
                      stroke={s.color}
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  );
                })}
              </g>
            ),
            ({ series, xScale, yScale }) => (
              <g>
                {series.map((s) =>
                  s.data.map((d, i) => (
                    <circle
                      key={`${s.id}-${i}`}
                      cx={xScale(d.data.x)}
                      cy={yScale(d.data.y)}
                      r={3}
                      fill="#FFFFFF"
                      stroke={s.color}
                      strokeWidth={2}
                    />
                  ))
                )}
              </g>
            ),
            "slices",
            "mesh",
          ]}
          axisBottom={{ tickSize: 0, tickPadding: 6, tickRotation: showQuarters ? -45 : 0 }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 6,
            format: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v),
          }}
          enableGridX={false}
          theme={chartTheme}
          legends={[
            {
              anchor: "bottom",
              direction: "row",
              translateY: showQuarters ? 70 : 50,
              itemWidth: 110,
              itemHeight: 18,
              symbolSize: 10,
              symbolShape: "circle",
            },
          ]}
          tooltip={({ point }) => (
            <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
              <Text fontWeight="700" color="gray.800">{point.serieId} · {point.data.x}</Text>
              <Text color={point.serieColor}>{Number(point.data.y).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} tCO₂e</Text>
            </Box>
          )}
          useMesh
        />
      </Box>
      <Text fontSize="11px" color="gray.500" mt={2}>
        One line per category. {showQuarters
          ? "Quarters without a finalised report are omitted — years reported at the yearly level only will not appear."
          : "Years without a finalised report are omitted from the line."}
      </Text>
    </Box>
  );
};

// Aggregate a category's rows by fuel type (consumptionType), summing real
// emissions — used as the sankey leaves.
const fuelLeaves = (rows) => {
  const map = new Map();
  (rows || []).forEach((r) => {
    const v = Number(r.emissions) || 0;
    if (v <= 0) return;
    const name = prettyLabel(r.consumptionType) || "Other";
    map.set(name, (map.get(name) || 0) + v);
  });
  return [...map.entries()].map(([name, value]) => ({ name, value }));
};

// Three-tier sankey: Scope 1 → category (Stationary/Mobile/Refrigerants) →
// fuel type. Built from real emissions; zero-weight branches are dropped
// (sankey rejects them).
const ScopeOneSankeyChart = ({ stationaryRows, mobileRows, refrigerantRows }) => {
  const { nodes, links, labels } = useMemo(() => {
    const cats = [
      { id: "Stationary", rows: stationaryRows, color: STATIONARY_ACCENT },
      { id: "Mobile", rows: mobileRows, color: MOBILE_ACCENT },
      { id: "Refrigerants", rows: refrigerantRows, color: REFRIGERANT_ACCENT },
    ];
    const builtNodes = [{ id: "Scope 1", nodeColor: TOTAL_ACCENT }];
    const builtLinks = [];

    cats.forEach((cat) => {
      const leaves = fuelLeaves(cat.rows);
      const catTotal = leaves.reduce((acc, l) => acc + l.value, 0);
      if (catTotal <= 0) return;
      builtNodes.push({ id: cat.id, nodeColor: cat.color });
      builtLinks.push({ source: "Scope 1", target: cat.id, value: Number(catTotal.toFixed(2)) });
      leaves.forEach((leaf) => {
        // Suffix the leaf with the category so two categories sharing a fuel
        // name don't collapse onto one node.
        const leafId = `${leaf.name} (${cat.id.slice(0, 3)})`;
        builtNodes.push({ id: leafId, nodeColor: cat.color });
        builtLinks.push({ source: cat.id, target: leafId, value: Number(leaf.value.toFixed(2)) });
      });
    });

    return toSafariSafeSankey(builtNodes, builtLinks);
  }, [stationaryRows, mobileRows, refrigerantRows]);

  // Height scales with the leaf (fuel-type) count — the right column has the
  // most nodes — so the bars/links stay readably thick rather than being
  // squeezed by inter-node spacing on a fixed height.
  const leafCount = links.filter((l) => labels.get(l.source) !== "Scope 1").length;
  const heightPx = Math.max(320, Math.min(480, leafCount * 15 + 90));

  if (nodes.length <= 1 || links.length === 0) {
    return (
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
        <Text textAlign="center" color="gray.500" fontSize="13px">
          No Scope 1 emissions data has been entered for this year.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} h={`${heightPx}px`}>
        <ResponsiveSankey
          data={{ nodes, links }}
          margin={{ top: 12, right: 180, bottom: 12, left: 110 }}
          align="justify"
          sort="input"
          colors={(node) => node.nodeColor}
          nodeOpacity={1}
          nodeHoverOthersOpacity={0.35}
          nodeThickness={14}
          nodeSpacing={8}
          nodeBorderWidth={0}
          nodeBorderRadius={2}
          linkOpacity={0.45}
          linkHoverOthersOpacity={0.1}
          linkContract={2}
          enableLinkGradient
          labelPosition="outside"
          labelOrientation="horizontal"
          labelPadding={10}
          labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
          label={(node) => labels.get(node.id) || node.id}
          theme={chartTheme}
          nodeTooltip={({ node }) => (
            <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
              <Text fontWeight="700" color="gray.800">{labels.get(node.id) || node.id}</Text>
              <Text color="gray.600">{Number(node.value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} tCO₂e</Text>
            </Box>
          )}
          linkTooltip={({ link }) => (
            <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
              <Text fontWeight="700" color="gray.800">
                {(labels.get(link.source.id) || link.source.id)} → {(labels.get(link.target.id) || link.target.id)}
              </Text>
              <Text color="gray.600">{Number(link.value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} tCO₂e</Text>
            </Box>
          )}
        />
      </Box>
      <Text fontSize="11px" color="gray.500" mt={2}>
        Fuel-type splits within each category use the recorded per-fuel emissions for this year.
      </Text>
    </Box>
  );
};

// Quarter-by-quarter table of the page's KPI-card figures (Total /
// Stationary / Mobile / Refrigerants). Mounted only while the Table view is
// selected, so its five-per-year queries fire lazily. Ignores the page's
// quarter filter — the quarters are the columns.
const ScopeOneQuarterlyTable = ({ year, dimensionParams }) => {
  const compare = useQuarterlyTableCompare();
  const currentQueries = useGetQuarterlyEmissions(year, dimensionParams, {
    retry: false,
    cacheTime: 60_000,
  });
  const prevQueries = useGetQuarterlyEmissions(year - 1, dimensionParams, {
    retry: false,
    cacheTime: 60_000,
    enabled: compare.needsPreviousYear,
  });
  const payloads = currentQueries.map((q) => (q.status === "success" ? q.data : null));
  const prevPayloads = prevQueries.map((q) => (q.status === "success" ? q.data : null));

  const ROWS = [
    { label: "Total", bold: true, get: (d) => d?.scope1Total },
    { label: "Stationary", get: (d) => sumEmissions(d?.scopeOneAndTwoDetail?.stationary) || null },
    { label: "Mobile", get: (d) => sumEmissions(d?.scopeOneAndTwoDetail?.mobile) || null },
    { label: "Refrigerants", get: (d) => sumEmissions(d?.scopeOneAndTwoDetail?.refrigerant) || null },
  ];
  const rows = ROWS.map((row) => ({
    label: row.label,
    bold: row.bold,
    values: payloads.map((d) => row.get(d)),
    prevValues: prevPayloads.map((d) => row.get(d)),
  }));

  const isLoading =
    currentQueries.some((q) => q.isLoading) ||
    (compare.needsPreviousYear && prevQueries.some((q) => q.isLoading));

  return <QuarterlyBreakdownTable year={year} rows={rows} isLoading={isLoading} compare={compare} />;
};

const ScopeOneChartViewToggle = ({ view, setView }) => {
  const options = [
    { label: "Trend", value: "trend", icon: <ChartLine size={14} /> },
    { label: "Flow", value: "sankey", icon: <FlowArrow size={14} /> },
    { label: "Table", value: "table", icon: <ListBullets size={14} /> },
  ];
  return (
    <Flex bg="gray.100" borderRadius="full" p="3px">
      {options.map((option) => {
        const active = view === option.value;
        return (
          <Button
            key={option.value}
            size="xs"
            borderRadius="full"
            bg={active ? "pureBlack" : "transparent"}
            color={active ? "white" : "gray.500"}
            fontWeight={active ? "600" : "normal"}
            rightIcon={option.icon}
            onClick={() => setView(option.value)}
            _hover={{ bg: active ? "pureBlack" : "gray.200" }}
            px={3}
          >
            {option.label}
          </Button>
        );
      })}
    </Flex>
  );
};

// Wraps the trend / flow / table views behind a toggle (mirrors Scope 2/3).
const ScopeOneVisualisation = ({ trendSeries, stationaryRows, mobileRows, refrigerantRows, trendQuarters, setTrendQuarters, year, dimensionParams }) => {
  const [view, setView] = useState("trend");
  const isTrend = view === "trend";
  const isTable = view === "table";
  return (
    <Box>
      <Flex justify="space-between" align="flex-end" mb={2} flexWrap="wrap" gap={3}>
        <Box>
          <Text fontSize="22px" fontWeight="700" color="darkTeal">
            {isTrend ? "5-year trend" : isTable ? "Emissions by quarter" : "Emissions flow"}
          </Text>
          <Text fontSize="13px" color="gray.500">
            {isTrend
              ? `Scope 1 direct emissions by category across the last five reporting years${trendQuarters ? ", split by quarter" : ""}.`
              : isTable
                ? `Scope 1 category figures for ${year}, split by quarter with a full-year total.`
                : "How Scope 1 splits across Stationary / Mobile / Refrigerants and into each fuel type."}
          </Text>
        </Box>
        <HStack spacing={3}>
          {isTrend && (
            <QuartersSwitch isChecked={trendQuarters} onChange={setTrendQuarters} />
          )}
          <ScopeOneChartViewToggle view={view} setView={setView} />
        </HStack>
      </Flex>
      {isTrend && <ScopeOneTrendChart trendSeries={trendSeries} showQuarters={trendQuarters} />}
      {isTable && <ScopeOneQuarterlyTable year={year} dimensionParams={dimensionParams} />}
      {!isTrend && !isTable && (
        <ScopeOneSankeyChart stationaryRows={stationaryRows} mobileRows={mobileRows} refrigerantRows={refrigerantRows} />
      )}
    </Box>
  );
};


// Coloured-accent KPI card — same component as Scope 2's, simplified for
// Scope 1: every card here treats a YoY decrease as positive (green).
const KpiCard = ({ accent, title, subtitle, tco2e, previousTco2e, footerKwh, footerTrailing, footerNote, loading }) => {
  const hasCurrent = typeof tco2e === "number";
  const hasPrev = typeof previousTco2e === "number" && previousTco2e !== 0;
  const pctChange = hasCurrent && hasPrev ? ((tco2e - previousTco2e) / previousTco2e) * 100 : null;

  return (
    <Box position="relative" pt={3}>
      <Box position="absolute" top={0} left={0} right={0} h="3px" bg={accent} borderRadius="full" />
      <HStack align="baseline" spacing={2} mb={2}>
        <Text fontSize="15px" fontWeight="700" color="darkTeal">{title}</Text>
        <Text fontSize="13px" color="gray.500">{subtitle}</Text>
      </HStack>
      <Box
        position="relative"
        display="inline-block"
        whiteSpace="nowrap"
        sx={{ "--big": "clamp(20px, 3vw, 56px)" }}
      >
        <Flex align="flex-end" gap={3}>
          <Text
            fontSize="var(--big)"
            fontWeight="700"
            color="darkTeal"
            lineHeight="1"
            letterSpacing="-0.02em"
          >
            {loading ? "…" : (hasCurrent ? formatTco2(tco2e) : "—")}
          </Text>
          <Flex direction="column" justify="space-between" alignSelf="stretch" flexShrink={0}>
            {pctChange != null ? <YoYChip pctChange={pctChange} /> : <Box />}
            <Text fontSize="calc(var(--big) * 0.36)" fontWeight="600" color="gray.600" lineHeight="1">
              tCO₂e
            </Text>
          </Flex>
        </Flex>
      </Box>
      {(footerKwh != null || footerTrailing) && (
        <HStack spacing={2} mt={3} align="baseline" flexWrap="wrap">
          {footerKwh != null && (
            <Text fontSize={{ base: "16px", lg: "20px", xl: "22px" }} fontWeight="700" color="darkTeal" lineHeight="1">
              {formatNumber(footerKwh, 0)}
            </Text>
          )}
          {footerTrailing && (
            <Text fontSize="13px" color="gray.500">{footerTrailing}</Text>
          )}
        </HStack>
      )}
      {footerNote && (
        <Text fontSize="12px" color="gray.500" mt={2}>{footerNote}</Text>
      )}
    </Box>
  );
};

// Tabbed wrapper: one tab per category that has rows. Each tab renders a
// per-category Total-by-fuel-type table (real data). A single By-dimension
// table/bars view (real per-dimension Scope 1 totals from the dimension
// report) sits above the per-category fuel tables — it is scope-wide, not
// per-category, so it renders once rather than inside each tab.
//
// A single Summary / Visual toggle lives above the tab strip — Visual swaps
// the by-dimension table for the by-dimension bars and the fuel tables for
// per-category fuel pies.
const CategoryTabs = ({
  categories,
  hideByDimension = false,
  dimensionRows = [],
  dimensionLevelName = null,
  unattributed = null,
  shownCount = 0,
  totalCount = null,
  isFetchingDimension = false,
  levelOptions = [],
  selectedLevelKey = null,
  autoLevelKey = null,
  onSelectLevel = null,
  dimQuarterly = false,
  setDimQuarterly = null,
  scopeOneCategoryIndex = new Map(),
  quarterlyDimContents = [],
  quarterlyItems = [],
  isFetchingQuarterlyDim = false,
  year = null,
}) => {
  const [view, setView] = useState("summary");
  // ESG-3959: measured per-dimension figures. These used to be share x org-wide total — the org's fuel
  // mix projected onto every site, unlabelled. The by-dimension report now carries the real split.
  const categoryDimensionRows = (c) => scopeOneCategoryRows(dimensionRows, c.key, scopeOneCategoryIndex);

  const quarterlyCategoryDimensionRows = (c) =>
    scopeOneQuarterlyCategoryRows(quarterlyDimContents, c.key, quarterlyItems);

  // Per-(item × category × period) emissions matrix for the stacked bar chart's
  // category filter. For each item and each present category, the emissions
  // per period, read straight off each period's measured breakdown (ESG-3959 — was share x total).
  const quarterlyDimByCategory = scopeOneQuarterlyByCategory(
    quarterlyDimContents,
    categories.map((c) => c.key),
    quarterlyItems
  );
  const categoryOptions = categories.map((c) => ({
    key: c.key,
    label: CATEGORY_META[c.key].label,
    color: CATEGORY_META[c.key].accent,
  }));

  if (categories.length === 0) return null;
  return (
    <Box>
      <Flex justify="flex-end" align="center" gap={3} mb={3}>
        {/* Annual/Quarterly sits immediately left of Summary/Visual so the two
            view switches are always adjacent. Only offered when there's a
            by-dimension table to apply it to. */}
        {setDimQuarterly && !hideByDimension && (
          <QuartersSwitch isChecked={dimQuarterly} onChange={setDimQuarterly} />
        )}
        <ConsumptionViewToggle selected={view} setSelected={setView} />
      </Flex>
      {view === "visual" ? (
        <Flex direction="column" gap={6}>
          {!hideByDimension && (
            <DimensionBarsPanel
              accent={STATIONARY_ACCENT}
              dimensionRows={dimensionRows}
              levelName={dimensionLevelName}
              loading={isFetchingDimension}
              quarterly={dimQuarterly}
              quarterlyDimByCategory={quarterlyDimByCategory}
              categoryOptions={categoryOptions}
              quarterlyLoading={isFetchingQuarterlyDim}
            />
          )}
          {categories.map((c) => (
            <CategoryVisualPanel
              key={c.key}
              accent={CATEGORY_META[c.key].accent}
              label={CATEGORY_META[c.key].label}
              currentRows={c.currentRows}
            />
          ))}
        </Flex>
      ) : (
        <Tabs variant="primary" isLazy>
          <TabList>
            {categories.map((c) => (
              <Tab key={c.key}>{CATEGORY_META[c.key].label}</Tab>
            ))}
          </TabList>
          <TabPanels>
            {categories.map((c) => (
              <TabPanel key={c.key} px={0} pt={6}>
                {!hideByDimension && (
                  <Box mb={6}>
                    <ScopeOneByDimensionTable
                      accent={CATEGORY_META[c.key].accent}
                      label={CATEGORY_META[c.key].label}
                      dimensionRows={categoryDimensionRows(c)}
                      levelName={dimensionLevelName}
                      loading={isFetchingDimension}
                      fuelRowsFor={(itemId) => scopeOneFuelRows(scopeOneCategoryIndex, itemId, c.key)}
                      consumptionUnit={c.unit}
                      unattributed={unattributed}
                      shownCount={shownCount}
                      totalCount={totalCount}
                      levelOptions={levelOptions}
                      selectedLevelKey={selectedLevelKey}
                      autoLevelKey={autoLevelKey}
                      onSelectLevel={onSelectLevel}
                      quarterly={dimQuarterly}
                      quarterlyRows={quarterlyCategoryDimensionRows(c)}
                      quarterlyLoading={isFetchingQuarterlyDim}
                      year={year}
                    />
                  </Box>
                )}
                <CategoryFuelTypeTable
                  accent={CATEGORY_META[c.key].accent}
                  label={CATEGORY_META[c.key].label}
                  currentRows={c.currentRows}
                  previousRows={c.previousRows}
                  dimensionRows={hideByDimension ? [] : dimensionRows}
                  levelName={dimensionLevelName}
                  fuelDimensionRowsFor={
                    hideByDimension
                      ? null
                      : (fuel, unit) =>
                          scopeOneDimensionRowsForFuel(scopeOneCategoryIndex, dimensionRows, c.key, fuel, unit)
                  }
                />
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      )}
    </Box>
  );
};

// Bars panel — real per-dimension Scope 1 emissions for the resolved next
// dimension level. One bar per dimension item, value = that item's
// scopeOneTotalCo2Emissions from the dimension report. Scope-wide (not
// per-category) since the report only carries a single Scope 1 total per
// item, so it renders once above the per-category fuel pies.
const FUEL_PIE_PALETTE = [
  "#F3B85E", "#B45309", "#5EB5F3", "#9A34F3", "#82D909",
  "#FC8181", "#9F7AEA", "#E53E3E", "#38B2AC", "#A0AEC0",
];

// Quarter-stack colours (Q1..Q4) + a distinct muted grey for the "Annual
// only" residual (year total − ΣQ1..Q4 — data entered at the yearly level).
const QUARTER_STACK_COLORS = ["#F3B85E", "#B45309", "#5EB5F3", "#2F855A"];
const ANNUAL_ONLY_COLOR = "#A0AEC0";
const STACK_KEYS = ["Q1", "Q2", "Q3", "Q4", "Annual only"];

const DimensionBarsPanel = ({ accent, dimensionRows, levelName, loading, quarterly = false, quarterlyDimByCategory = [], categoryOptions = [], quarterlyLoading = false }) => {
  // Category filter for the stacked bars (Stationary / Mobile / Refrigerants).
  // All categories on by default; the pills toggle which contribute to the
  // stack. Selecting none falls back to all (an empty chart is never useful).
  const [visibleCats, setVisibleCats] = useState(() => new Set(categoryOptions.map((c) => c.key)));
  const catKeysSig = categoryOptions.map((c) => c.key).join("|");
  useEffect(() => {
    setVisibleCats(new Set(categoryOptions.map((c) => c.key)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catKeysSig]);
  const toggleCat = (key) =>
    setVisibleCats((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const activeCats = visibleCats.size > 0 ? visibleCats : new Set(categoryOptions.map((c) => c.key));

  // Annual (single-series) bar data.
  //
  // ESG-3960: `dimension` is the item ID, not its name. Nivo's indexBy must be unique, and two ACTIVE
  // items in one dimension may share a name — duplicate index keys merge or drop bars, which is a wrong
  // figure rather than merely an ambiguous label. The axis and tooltip resolve the id back to the name
  // through labelById, so twins render as two bars with the same label, matching the table.
  const labelById = new Map(
    [...(dimensionRows || []), ...(quarterlyDimByCategory || [])].map((r) => [String(r.id), r.name])
  );
  const labelFor = (id) => labelById.get(String(id)) ?? String(id);
  const barData = (dimensionRows || []).map((r) => ({
    dimension: String(r.id),
    Emissions: Number((r.value || 0).toFixed(2)),
  }));

  // Quarterly stacked bar data: one bar per dimension item, segments Q1..Q4
  // plus an "Annual only" residual so the stack height = the whole-year
  // total (which may exceed ΣQ1..Q4 when data was entered yearly). Each
  // period is summed over ONLY the categories the filter pills keep active,
  // using the per-(item × category × period) matrix.
  // ESG-3959: driven straight off the per-(item x category x period) matrix, which already carries the
  // items in display order — the separate share list it used to be zipped against is gone.
  const stackedData = (quarterlyDimByCategory || []).map((item) => {
    const byCategory = item.byCategory || {};
    // Sum active categories per period → [q1..q4, year].
    const filtered = [0, 1, 2, 3, 4].map((pi) =>
      categoryOptions.reduce(
        (acc, c) => acc + (activeCats.has(c.key) ? (byCategory[c.key]?.[pi] || 0) : 0),
        0
      )
    );
    const [q1, q2, q3, q4, yearTotal] = filtered;
    const qSum = q1 + q2 + q3 + q4;
    const annualOnly = Math.max(0, yearTotal - qSum);
    return {
      dimension: String(item.id),
      Q1: Number(q1.toFixed(2)),
      Q2: Number(q2.toFixed(2)),
      Q3: Number(q3.toFixed(2)),
      Q4: Number(q4.toFixed(2)),
      "Annual only": Number(annualOnly.toFixed(2)),
      _total: yearTotal,
    };
  });
  const stackColorByKey = {
    Q1: QUARTER_STACK_COLORS[0],
    Q2: QUARTER_STACK_COLORS[1],
    Q3: QUARTER_STACK_COLORS[2],
    Q4: QUARTER_STACK_COLORS[3],
    "Annual only": ANNUAL_ONLY_COLOR,
  };
  const isLoading = quarterly ? quarterlyLoading : loading;
  const hasData = quarterly ? stackedData.length > 0 : barData.length > 0;

  return (
    <Box>
      <HStack spacing={2} mb={3}>
        <Box w="8px" h="8px" bg={accent} borderRadius="sm" />
        <Text fontSize="14px" fontWeight="700" color="darkTeal" textTransform="uppercase" letterSpacing="0.04em">
          {`Scope 1 by ${levelName ? levelName.toLowerCase() : "dimension"}`}
        </Text>
      </HStack>
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
        <Flex justify="space-between" align="center" mb={quarterly && categoryOptions.length > 1 ? 3 : 1} gap={3} flexWrap="wrap">
          <Text fontSize="11px" color={accent} textTransform="uppercase" letterSpacing="0.04em" fontWeight="700">
            Emissions (tCO₂e){quarterly ? " · stacked by quarter" : ""}
          </Text>
          {quarterly && categoryOptions.length > 1 && (
            <HStack spacing={2} flexWrap="wrap">
              {categoryOptions.map((c) => {
                const active = activeCats.has(c.key);
                return (
                  <Tag
                    key={c.key}
                    size="sm"
                    borderRadius="full"
                    cursor="pointer"
                    border="1px solid"
                    borderColor={active ? c.color : "gray.300"}
                    bg={active ? `${c.color}22` : "transparent"}
                    color={active ? "gray.700" : "gray.500"}
                    fontWeight="600"
                    onClick={() => !active && toggleCat(c.key)}
                    _hover={{ bg: active ? `${c.color}22` : "gray.50" }}
                    transition="all 0.15s"
                  >
                    <TagLabel>{c.label}</TagLabel>
                    {active && <TagCloseButton onClick={(e) => { e.stopPropagation(); toggleCat(c.key); }} />}
                  </Tag>
                );
              })}
            </HStack>
          )}
        </Flex>
        {isLoading ? (
          <Flex h="280px" align="center" justify="center" color="gray.400" fontSize="13px">Loading…</Flex>
        ) : !hasData ? (
          <Flex h="280px" align="center" justify="center" color="gray.400" fontSize="13px">
            {levelName ? `No ${levelName.toLowerCase()} assigned to this data` : "No dimension breakdown available"}
          </Flex>
        ) : quarterly ? (
          <Box h="280px">
            <ResponsiveBar
              data={stackedData}
              keys={STACK_KEYS}
              indexBy="dimension"
              margin={{ top: 8, right: 16, bottom: 56, left: 64 }}
              padding={0.35}
              colors={({ id }) => stackColorByKey[id]}
              axisBottom={{ tickSize: 0, tickPadding: 6, tickRotation: -20, format: labelFor }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 6,
                format: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v),
              }}
              enableLabel={false}
              borderRadius={2}
              theme={chartTheme}
              tooltip={({ id, value, indexValue, data }) => (
                <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
                  <Text fontWeight="700" color="gray.800">{labelFor(indexValue)}</Text>
                  <Text color="gray.600">
                    <Text as="span" fontWeight="600" color={stackColorByKey[id]}>{id}</Text>: {formatTco2(value)} tCO₂e
                  </Text>
                  <Text color="gray.500" fontSize="11px" mt={1}>Year total: {formatTco2(data._total)} tCO₂e</Text>
                </Box>
              )}
            />
          </Box>
        ) : (
          <Box h="280px">
            <ResponsiveBar
              data={barData}
              keys={["Emissions"]}
              indexBy="dimension"
              margin={{ top: 8, right: 16, bottom: 56, left: 64 }}
              padding={0.35}
              colors={accent}
              axisBottom={{ tickSize: 0, tickPadding: 6, tickRotation: -20, format: labelFor }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 6,
                format: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v),
              }}
              enableLabel={false}
              borderRadius={2}
              theme={chartTheme}
              tooltip={({ value, indexValue }) => (
                <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
                  <Text fontWeight="700" color="gray.800">{labelFor(indexValue)}</Text>
                  <Text color="gray.600">{formatTco2(value)} tCO₂e</Text>
                </Box>
              )}
            />
          </Box>
        )}
        {quarterly && hasData && (
          <Flex wrap="wrap" gap={4} mt={3} justify="center">
            {STACK_KEYS.map((k) => (
              <Flex key={k} align="center" gap={1.5}>
                <Box w="10px" h="10px" borderRadius="sm" bg={stackColorByKey[k]} />
                <Text fontSize="11px" color="gray.600">{k}</Text>
              </Flex>
            ))}
          </Flex>
        )}
        {quarterly && hasData && (
          <Text fontSize="11px" color="gray.500" mt={2}>
            Each bar stacks the four quarters plus any &ldquo;Annual only&rdquo; emissions entered at the yearly level — the full bar height is the year total.
          </Text>
        )}
      </Box>
    </Box>
  );
};

// Visual panel for a single category — fuel-type share of the category's
// emissions as a pie. Direct from the real flat fuel rows.
const CategoryVisualPanel = ({ accent, label, currentRows }) => {
  const fuels = (() => {
    const map = new Map();
    (currentRows || []).forEach((r) => {
      const key = `${prettyLabel(r.consumptionType)} (${r.measurementUnit || ""})`;
      map.set(key, (map.get(key) || 0) + (Number(r.emissions) || 0));
    });
    return [...map.entries()]
      .map(([id, value]) => ({ id, value }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value);
  })();
  const fuelTotal = fuels.reduce((acc, f) => acc + f.value, 0);
  const colorByIndex = fuels.map((_, i) => FUEL_PIE_PALETTE[i % FUEL_PIE_PALETTE.length]);

  return (
    <Box>
      <HStack spacing={2} mb={3}>
        <Box w="8px" h="8px" bg={accent} borderRadius="sm" />
        <Text fontSize="14px" fontWeight="700" color="darkTeal" textTransform="uppercase" letterSpacing="0.04em">
          {label} Combustion
        </Text>
      </HStack>
      <SimpleGrid columns={1} spacing={4}>
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
          <Text fontSize="13px" fontWeight="600" color="gray.700" mb={3}>
            Total fuel by type{" "}
            <Text as="span" fontSize="11px" color="gray.400" fontWeight="500">tCO₂e share</Text>
          </Text>
          {fuels.length === 0 ? (
            <Flex h="320px" align="center" justify="center" color="gray.400" fontSize="13px">No data</Flex>
          ) : (
            <Flex gap={4} align="center" flexWrap={{ base: "wrap", md: "nowrap" }}>
              <Box w={{ base: "100%", md: "200px" }} h="200px" flexShrink={0}>
                <ResponsivePie
                  data={fuels}
                  margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                  innerRadius={0.6}
                  padAngle={1}
                  cornerRadius={2}
                  activeOuterRadiusOffset={4}
                  colors={colorByIndex}
                  borderWidth={1}
                  borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                  enableArcLabels={false}
                  enableArcLinkLabels={false}
                  tooltip={({ datum }) => (
                    <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
                      <Text fontWeight="700" color="gray.800">{datum.id}</Text>
                      <Text color="gray.600">{formatTco2(datum.value)} tCO₂e</Text>
                    </Box>
                  )}
                  theme={chartTheme}
                />
              </Box>
              <Box flex="1" minW={0}>
                <Flex direction="column" gap={1.5}>
                  {fuels.map((f, i) => {
                    const pct = fuelTotal > 0 ? (f.value / fuelTotal) * 100 : 0;
                    return (
                      <Flex key={f.id} align="center" gap={2}>
                        <Box w="10px" h="10px" borderRadius="sm" bg={colorByIndex[i]} flexShrink={0} />
                        <Text flex="1" fontSize="12px" color="gray.700" isTruncated>{f.id}</Text>
                        <Text fontSize="12px" color="gray.700" fontWeight="600">{formatTco2(f.value)}</Text>
                        <Text fontSize="11px" color="gray.500" minW="44px" textAlign="right">{pct.toFixed(1)}%</Text>
                      </Flex>
                    );
                  })}
                </Flex>
              </Box>
            </Flex>
          )}
        </Box>
      </SimpleGrid>
    </Box>
  );
};

// ESG-3959: buildRowFuelBreakdown used to live here, scaling the category's org-wide fuel rows by the
// dimension row's share of that category. The by-dimension report now carries the real per-item fuel
// split, so the expanded rows read it directly — see scopeOneFuelRows in utils/scopeOneDimensions.

// Per-category "By dimension" table — one row per dimension item at the selected level, carrying that
// item's own measured figures for the category. Each row expands to its real per-fuel split.
const ScopeOneByDimensionTable = ({ accent, label, dimensionRows, levelName, loading, fuelRowsFor = null, consumptionUnit, unattributed = null, shownCount = 0, totalCount = null, levelOptions = [], selectedLevelKey = null, autoLevelKey = null, onSelectLevel = null, quarterly = false, quarterlyRows = [], quarterlyLoading = false, year = null }) => {
  const total = (dimensionRows || []).reduce((acc, r) => acc + (r.value || 0), 0);
  const totalConsumption = (dimensionRows || []).reduce((acc, r) => acc + (r.consumption || 0), 0);
  // kWh-equivalent is unit-normalised, so it can be totalled (null when no row carries one).
  const totalKwh = sumKwh(dimensionRows);
  // ESG-3960: expansion state keyed by dimension item ID. Keyed by name, expanding one of two same-name
  // items expanded both.
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  const rowIds = (dimensionRows || []).map((r) => r.id);
  const openCount = rowIds.filter((id) => expanded[id]).length;
  const expandAll = () => setExpanded(Object.fromEntries(rowIds.map((id) => [id, true])));
  const collapseAll = () => setExpanded({});

  // The "By {level}" title is a dropdown so the breakdown level can be chosen independently of the
  // page's dimension filter. "Auto" is the level below the deepest filtered level.
  const levelSelectable = onSelectLevel && levelOptions.length > 0;
  const levelLabel = levelName ? levelName.toLowerCase() : "dimension";
  const truncated = totalCount != null && shownCount > 0 && totalCount > shownCount;

  return (
    <Box>
      <Flex justify="space-between" align="flex-end" mb={3}>
        {levelSelectable ? (
          <Menu>
            <MenuButton
              as={Button}
              variant="ghost"
              px={1}
              h="auto"
              fontSize="22px"
              fontWeight="700"
              color="darkTeal"
              rightIcon={<CaretDown size={16} weight="bold" />}
              _hover={{ bg: "gray.50" }}
              _active={{ bg: "gray.100" }}
            >
              By {levelName ? levelName.toLowerCase() : "dimension"}
            </MenuButton>
            <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1}>
              <MenuItem
                onClick={() => onSelectLevel(null)}
                fontWeight={selectedLevelKey === autoLevelKey ? "bold" : "normal"}
              >
                <Flex align="center" gap={2} w="full" justify="space-between">
                  <Text>Auto</Text>
                  <Text fontSize="11px" color="gray.400">
                    level below filter
                  </Text>
                </Flex>
              </MenuItem>
              {levelOptions.map((opt) => (
                <MenuItem
                  key={opt.key}
                  onClick={() => onSelectLevel(opt.key)}
                  fontWeight={selectedLevelKey === opt.key ? "bold" : "normal"}
                >
                  {opt.name}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        ) : (
          <Text fontSize="22px" fontWeight="700" color="darkTeal">By {levelName ? levelName.toLowerCase() : "dimension"}</Text>
        )}
        <Text fontSize="13px" color="gray.500">
          {quarterly
            ? `${label} emissions per ${levelName ? levelName.toLowerCase() : "dimension item"} by quarter, with real kWh-equivalent in brackets.`
            : `${label} emissions per ${levelName ? levelName.toLowerCase() : "dimension item"} · click a row for its fuel breakdown.`}
        </Text>
      </Flex>
      {quarterly ? (
        <ScopeOneByDimensionQuarterlyTable
          accent={accent}
          label={label}
          levelName={levelName}
          rows={quarterlyRows}
          loading={quarterlyLoading}
          year={year}
        />
      ) : (
      <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
        <Table size="sm" variant="unstyled">
          <Thead>
            <Tr bg="gray.50">
              <Th py={3} px={4} colSpan={2} textAlign="left" fontSize="11px" color={accent} textTransform="uppercase" letterSpacing="0.04em" fontWeight="700">
                <HStack spacing={2}>
                  <Box w="8px" h="8px" bg={accent} borderRadius="sm" />
                  <Text>{levelName ? levelName.toUpperCase() : "DIMENSION"}</Text>
                  {rowIds.length > 0 && (
                    <ExpandCollapseControl
                      allExpanded={openCount === rowIds.length}
                      onExpandAll={expandAll}
                      onCollapseAll={collapseAll}
                    />
                  )}
                </HStack>
              </Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">{`Consumption (${consumptionUnit || "units"})`}</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Consumption (kWh equivalent)</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Emissions (tCO₂e)</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">{`Share of ${label}`}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr><Td colSpan={6} py={6} textAlign="center" color="gray.400" fontSize="13px">Loading…</Td></Tr>
            ) : (dimensionRows || []).length === 0 ? (
              // ESG-3960: level-aware. An empty table at a level whose rows carry no item there means
              // "nothing is tagged at this level", not "no emissions" — and the two read very differently
              // to someone checking their footprint.
              <Tr><Td colSpan={6} py={6} textAlign="center" color="gray.400" fontSize="13px">
                {levelName ? `No ${levelLabel} assigned to this data` : "No dimension breakdown available"}
              </Td></Tr>
            ) : dimensionRows.map((row, idx) => {
              const share = total > 0 ? row.value / total : 0;
              const isOpen = !!expanded[row.id];
              // ESG-3959: this item's OWN measured fuel rows. `share` above is still used, but only for
              // the "Share of" column — it no longer scales any figure.
              const fuels = isOpen && fuelRowsFor ? fuelRowsFor(row.id) : [];
              return (
                <Fragment key={row.id}>
                  <Tr
                    borderTop={idx === 0 ? "none" : "1px solid"}
                    borderColor="gray.100"
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                    onClick={() => toggle(row.id)}
                  >
                    <Td py={4} pl={4} pr={1} width="32px">
                      <IconButton
                        size="xs"
                        variant="ghost"
                        aria-label={isOpen ? "Collapse" : "Expand"}
                        icon={isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                        onClick={(e) => { e.stopPropagation(); toggle(row.id); }}
                      />
                    </Td>
                    <Td py={4} pl={1} pr={4} fontSize="14px" color="gray.700" fontWeight="500" textAlign="left">
                      <HStack spacing={2}>
                        <Box w="8px" h="8px" bg={accent} borderRadius="sm" />
                        <Text>{row.name}</Text>
                      </HStack>
                    </Td>
                    <Td py={4} px={4} fontSize="14px" color="gray.700" fontWeight="600" textAlign="right">
                      {formatNumber(row.consumption, 0)}
                    </Td>
                    <Td py={4} px={4} fontSize="14px" color="gray.700" fontWeight="600" textAlign="right">
                      {row.kwh != null ? formatNumber(row.kwh, 0) : "—"}
                    </Td>
                    <Td py={4} px={4} fontSize="14px" color={accent} fontWeight="700" textAlign="right">
                      {formatTco2(row.value)}
                    </Td>
                    <Td py={4} px={4} textAlign="left" minW="220px">
                      <HStack spacing={3}>
                        <Box flex="1" h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                          <Box h="100%" w={`${share * 100}%`} bg={accent} borderRadius="full" />
                        </Box>
                        <Text fontSize="13px" color="gray.700" fontWeight="600" minW="48px" textAlign="right">
                          {(share * 100).toFixed(1)}%
                        </Text>
                      </HStack>
                    </Td>
                  </Tr>
                  {isOpen && (
                    <>
                      {/* Sub-header row, anchored to the parent's columns so
                          fuel rows line up with Consumption / Emissions in
                          the main table. */}
                      <Tr bg="gray.50">
                        <Td py={2} pl={4} pr={1} width="32px" />
                        <Td py={2} pl={1} pr={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Fuel type</Td>
                        <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Consumption</Td>
                        <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">kWh equivalent</Td>
                        <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Emissions (tCO₂e)</Td>
                        <Td py={2} px={4} />
                      </Tr>
                      {fuels.length === 0 ? (
                        <Tr bg="gray.50">
                          <Td colSpan={6} py={3} textAlign="center" color="gray.400" fontSize="13px">No fuel data</Td>
                        </Tr>
                      ) : fuels.map((f, fIdx) => (
                        <Tr key={`${row.id}-${fIdx}`} bg="gray.50">
                          <Td py={2} pl={4} pr={1} width="32px" />
                          <Td py={2} pl={1} pr={4} fontSize="13px" color="gray.700">
                            <HStack spacing={2}>
                              <Box w="8px" h="8px" bg={accent} borderRadius="sm" />
                              <Text fontWeight="500">{f.fuel}</Text>
                              <Text color="gray.400" fontSize="12px">{f.unit || "—"}</Text>
                            </HStack>
                          </Td>
                          <Td py={2} px={4} fontSize="13px" color="gray.700" fontWeight="600" textAlign="right">{formatNumber(f.consumption, 0)}</Td>
                          <Td py={2} px={4} fontSize="13px" color="gray.700" fontWeight="600" textAlign="right">{f.kwh != null ? formatNumber(f.kwh, 0) : "—"}</Td>
                          <Td py={2} px={4} fontSize="13px" color={accent} fontWeight="600" textAlign="right">{formatTco2(f.emissions)}</Td>
                          <Td py={2} px={4} />
                        </Tr>
                      ))}
                    </>
                  )}
                </Fragment>
              );
            })}
            {/* ESG-3960: emissions the report holds that carry NO dimension item at this level, so they
                belong to none of the rows above. Rendered as its own row rather than folded into Total,
                and never as a chart series — it is an absence, not a category. */}
            {unattributed != null && (
              <Tr borderTop="1px solid" borderColor="gray.100" bg="orange.50">
                <Td py={4} pl={4} pr={1} width="32px" />
                <Td py={4} pl={1} pr={4} fontSize="14px" color="gray.600" fontWeight="500" textAlign="left">
                  <HStack spacing={2}>
                    <Box w="8px" h="8px" bg="gray.300" borderRadius="sm" />
                    <Text fontStyle="italic">{`No ${levelLabel} assigned`}</Text>
                  </HStack>
                </Td>
                <Td py={4} px={4} textAlign="right" color="gray.400" fontSize="14px">—</Td>
                <Td py={4} px={4} textAlign="right" color="gray.400" fontSize="14px">—</Td>
                <Td py={4} px={4} fontSize="14px" color="gray.600" fontWeight="700" textAlign="right">
                  {formatTco2(unattributed)}
                </Td>
                <Td py={4} px={4} textAlign="left" minW="220px">
                  <Text fontSize="11px" color="gray.500">Scope 1 not attributed at this level</Text>
                </Td>
              </Tr>
            )}
            {(dimensionRows || []).length > 0 && (
              <Tr bg="gray.50" borderTop="2px solid" borderColor="gray.200">
                <Td py={4} pl={4} pr={1} width="32px" />
                <Td py={4} pl={1} pr={4} fontSize="13px" color="darkTeal" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" textAlign="left">Total</Td>
                <Td py={4} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{formatNumber(totalConsumption, 0)}</Td>
                <Td py={4} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{totalKwh != null ? formatNumber(totalKwh, 0) : "—"}</Td>
                <Td py={4} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{formatTco2(total)}</Td>
                <Td py={4} px={4} textAlign="left" minW="220px">
                  <Text fontSize="13px" color="gray.700" fontWeight="700" textAlign="right">100%</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
      )}
      {/* ESG-3960: the non-quarterly line used to end "fuel breakdowns scale the category's recorded
          fuel mix to each row's share" — true of the apportionment ESG-3959 deleted, and false since. */}
      <Text fontSize="11px" color="gray.500" mt={2}>
        {quarterly
          ? `Each quarter column is that quarter's own measured per-${levelLabel} figure. The year total is the whole-year figure and may exceed the sum of the four quarters when data was entered at the yearly level. kWh-equivalent in brackets is null (—) where a fuel has no calorific factor.`
          : `Every figure is measured for that ${levelLabel} — consumption, kWh-equivalent and the per-fuel breakdown all come from its own rows, none of it apportioned from an org-wide mix.`}
        {truncated ? ` Showing the top ${shownCount} of ${totalCount} ${levelLabel}s by emissions.` : ""}
        {unattributed != null
          ? ` ${formatTco2(unattributed)} tCO₂e carries no ${levelLabel} and is listed separately; the rows above do not include it.`
          : ""}
      </Text>
    </Box>
  );
};

// Quarter-by-quarter variant of the by-dimension table. One row
// per dimension item; columns are Q1..Q4 + a whole-year total. Each cell
// shows emissions with the real kWh-equivalent bracketed beside it. No
// share-of-category column (dropped to keep the wider layout readable). The
// year total is the ALL-period figure and may exceed ΣQ1..Q4 when data was
// entered at the yearly level — flagged in the parent's caveat note.
const PERIOD_HEADS = ["Q1", "Q2", "Q3", "Q4", "Year"];
const fmtQuarterCell = (p) => {
  if (!p || !(p.emissions > 0)) return "—";
  const emis = formatTco2(p.emissions);
  const kwh = p.kwh != null && p.kwh > 0 ? formatNumber(p.kwh, 0) : null;
  return kwh ? `${emis} (${kwh})` : emis;
};
const ScopeOneByDimensionQuarterlyTable = ({ accent, label, levelName, rows = [], loading = false, year = null }) => {
  // ESG-3960: keyed by dimension item ID — keyed by name, expanding one of two same-name items opened both.
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  const expandableIds = rows.filter((r) => (r.fuels || []).length > 0).map((r) => r.id);
  const openCount = expandableIds.filter((id) => expanded[id]).length;
  const expandAll = () => setExpanded(Object.fromEntries(expandableIds.map((id) => [id, true])));
  const collapseAll = () => setExpanded({});

  // Column totals across the shown rows, per period.
  const colTotals = PERIOD_HEADS.map((_, pi) =>
    rows.reduce(
      (acc, r) => ({
        emissions: acc.emissions + (r.periods[pi]?.emissions || 0),
        kwh: acc.kwh + (r.periods[pi]?.kwh || 0),
      }),
      { emissions: 0, kwh: 0 }
    )
  );
  return (
    <Box>
      <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
        <Table size="sm" variant="unstyled">
          <Thead>
            <Tr bg="gray.50">
              <Th py={3} px={4} colSpan={2} fontSize="11px" color={accent} textTransform="uppercase" letterSpacing="0.04em" fontWeight="700" textAlign="left">
                <HStack spacing={2}>
                  <Box w="8px" h="8px" bg={accent} borderRadius="sm" />
                  <Text>{levelName ? levelName.toUpperCase() : "DIMENSION"}</Text>
                  {expandableIds.length > 0 && (
                    <ExpandCollapseControl
                      allExpanded={openCount === expandableIds.length}
                      onExpandAll={expandAll}
                      onCollapseAll={collapseAll}
                    />
                  )}
                </HStack>
              </Th>
              {PERIOD_HEADS.map((h) => (
                <Th key={h} py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">
                  {h === "Year" ? `${year || ""} Total` : h}
                  <Text as="span" fontSize="9px" color="gray.400" fontWeight="500" textTransform="none"> tCO₂e (kWh)</Text>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr><Td colSpan={7} py={6} textAlign="center" color="gray.400" fontSize="13px">Loading…</Td></Tr>
            ) : rows.length === 0 ? (
              <Tr><Td colSpan={7} py={6} textAlign="center" color="gray.400" fontSize="13px">No quarterly {label.toLowerCase()} data for this year.</Td></Tr>
            ) : (
              rows.map((row, idx) => {
                const hasFuels = (row.fuels || []).length > 0;
                const isOpen = hasFuels && !!expanded[row.id];
                return (
                  <Fragment key={row.id}>
                    <Tr
                      borderTop={idx === 0 ? "none" : "1px solid"}
                      borderColor="gray.100"
                      cursor={hasFuels ? "pointer" : undefined}
                      _hover={hasFuels ? { bg: "gray.50" } : undefined}
                      onClick={hasFuels ? () => toggle(row.id) : undefined}
                    >
                      <Td py={4} pl={4} pr={1} width="32px">
                        {hasFuels && (
                          <IconButton
                            size="xs"
                            variant="ghost"
                            aria-label={isOpen ? "Collapse" : "Expand"}
                            icon={isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                            onClick={(e) => { e.stopPropagation(); toggle(row.id); }}
                          />
                        )}
                      </Td>
                      <Td py={4} pl={1} pr={4} fontSize="14px" color="gray.700" fontWeight="500" textAlign="left">
                        <HStack spacing={2}>
                          <Box w="8px" h="8px" bg={accent} borderRadius="sm" />
                          <Text>{row.name}</Text>
                        </HStack>
                      </Td>
                      {row.periods.map((p, pi) => (
                        <Td key={pi} py={4} px={4} fontSize="13px" fontWeight={pi === 4 ? "700" : "600"} color={pi === 4 ? accent : "gray.700"} textAlign="right">
                          {fmtQuarterCell(p)}
                        </Td>
                      ))}
                    </Tr>
                    {isOpen && (
                      <>
                        <Tr bg="gray.50">
                          <Td py={2} pl={12} pr={4} colSpan={2} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="700">
                            Fuel type
                          </Td>
                          {PERIOD_HEADS.map((h) => (
                            <Td key={h} py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="700" textAlign="right">
                              {h === "Year" ? "Total" : h}
                            </Td>
                          ))}
                        </Tr>
                        {row.fuels.map((f) => (
                          <Tr key={`${f.fuel}|${f.unit}`} bg="gray.50">
                            <Td py={2.5} pl={12} pr={4} colSpan={2} fontSize="13px" color="gray.600">
                              <HStack spacing={2}>
                                <Box w="6px" h="6px" bg={accent} opacity={0.6} borderRadius="sm" />
                                <Text>{f.fuel}{f.unit ? ` · ${f.unit}` : ""}</Text>
                              </HStack>
                            </Td>
                            {f.periods.map((p, pi) => (
                              <Td key={pi} py={2.5} px={4} fontSize="12px" fontWeight={pi === 4 ? "700" : "600"} color={pi === 4 ? accent : "gray.600"} textAlign="right">
                                {fmtQuarterCell(p)}
                              </Td>
                            ))}
                          </Tr>
                        ))}
                      </>
                    )}
                  </Fragment>
                );
              })
            )}
            {!loading && rows.length > 0 && (
              <Tr bg="gray.50" borderTop="2px solid" borderColor="gray.200">
                <Td py={4} px={4} colSpan={2} fontSize="13px" color="darkTeal" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" textAlign="left">Total</Td>
                {colTotals.map((t, pi) => (
                  <Td key={pi} py={4} px={4} fontSize="13px" color="darkTeal" fontWeight="700" textAlign="right">
                    {t.emissions > 0 ? fmtQuarterCell(t) : "—"}
                  </Td>
                ))}
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

// Real-data table — aggregates the category's flat rows by fuel-type+unit,
// sorts by emissions desc, with progress bars and an honest YoY % computed
// from previousYearData. "Share" is the fuel's share within this category
// (not the whole of Scope 1).
const CategoryFuelTypeTable = ({ accent, label, currentRows, previousRows, dimensionRows = [], levelName = null, fuelDimensionRowsFor = null }) => {
  // Per-fuel dimension expansion state, keyed by fuel|unit.
  const [expanded, setExpanded] = useState({});
  const toggleRow = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  // Expansion follows the SAME level as the By-dimension table's selector — dimensionRows are the rows
  // already resolved/filtered for that level.
  //
  // ESG-3960: this used to split each fuel across the level by each item's SHARE of total emissions —
  // the fifth apportionment site, missed by ESG-3959 because it runs fuel → dimensions rather than
  // dimension → fuels. It asserted that every site burned each fuel in proportion to its overall
  // footprint, so a site that burns none of a fuel still showed a figure for it. The by-dimension report
  // now carries the real per-item fuel rows, so this reads them.
  const expandable = !!fuelDimensionRowsFor && (dimensionRows || []).length > 0;
  const buildFuelDimensionSplit = (fuel) =>
    fuelDimensionRowsFor ? fuelDimensionRowsFor(fuel.fuel, fuel.unit) : [];

  const aggregate = (rows) => {
    const map = new Map();
    (rows || []).forEach((r) => {
      const key = `${prettyLabel(r.consumptionType)}|${r.measurementUnit || ""}`;
      const existing = map.get(key) || {
        fuel: prettyLabel(r.consumptionType),
        unit: r.measurementUnit || "",
        consumption: 0,
        emissions: 0,
        kwh: null,
      };
      existing.consumption += Number(r.consumption) || 0;
      existing.emissions   += Number(r.emissions)   || 0;
      // Accumulate the kWh-equivalent only for rows that carry one; leave null when none do so
      // the row can render "—" rather than a misleading 0.
      if (r.kwh != null && Number.isFinite(Number(r.kwh))) {
        existing.kwh = (existing.kwh || 0) + Number(r.kwh);
      }
      map.set(key, existing);
    });
    return map;
  };

  const current = aggregate(currentRows);
  const previous = aggregate(previousRows);
  const totalEmissions = [...current.values()].reduce((acc, r) => acc + r.emissions, 0);
  // kWh-equivalent normalises to a single unit, so unlike raw consumption it can be totalled.
  const totalKwh = sumKwh(currentRows);
  const sorted = [...current.values()].sort((a, b) => b.emissions - a.emissions);
  const rowKeys = sorted.map((r) => `${r.fuel}|${r.unit}`);
  const openCount = rowKeys.filter((k) => expanded[k]).length;
  const expandAll = () => setExpanded(Object.fromEntries(rowKeys.map((k) => [k, true])));
  const collapseAll = () => setExpanded({});

  return (
    <Box>
      <Flex justify="space-between" align="flex-end" mb={3}>
        <Text fontSize="22px" fontWeight="700" color="darkTeal">Total by fuel type</Text>
        <Text fontSize="13px" color="gray.500">
          Aggregated across all dimensions · sorted by emissions
          {expandable ? ` · click a row for its ${(levelName || "dimension").toLowerCase()} breakdown` : ""}
        </Text>
      </Flex>
      <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
        <Table size="sm" variant="unstyled">
          <Thead>
            <Tr bg="gray.50">
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">
                <HStack spacing={2}>
                  <Text>Fuel type</Text>
                  {expandable && rowKeys.length > 0 && (
                    <ExpandCollapseControl
                      allExpanded={openCount === rowKeys.length}
                      onExpandAll={expandAll}
                      onCollapseAll={collapseAll}
                    />
                  )}
                </HStack>
              </Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Unit</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Total Consumption</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Consumption (kWh equivalent)</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Total Emissions (tCO₂e)</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">{`Share of ${label}`}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {sorted.length === 0 ? (
              <Tr><Td colSpan={6} py={6} textAlign="center" color="gray.400">No fuel data</Td></Tr>
            ) : sorted.map((r, idx) => {
              const key = `${r.fuel}|${r.unit}`;
              const prev = previous.get(key);
              const pctChange = (prev && prev.emissions > 0)
                ? ((r.emissions - prev.emissions) / prev.emissions) * 100
                : null;
              const share = totalEmissions > 0 ? (r.emissions / totalEmissions) * 100 : 0;
              const isOpen = expandable && !!expanded[key];
              const dimSplit = isOpen ? buildFuelDimensionSplit(r) : [];
              // Share of THIS fuel across the levels that actually burn it. Derived from the measured
              // rows rather than being the input to them, as it was before ESG-3960.
              const dimSplitTotal = dimSplit.reduce((acc, d) => acc + (d.emissions || 0), 0);
              return (
                <Fragment key={key}>
                  <Tr
                    borderTop={idx === 0 ? "none" : "1px solid"}
                    borderColor="gray.100"
                    cursor={expandable ? "pointer" : undefined}
                    _hover={expandable ? { bg: "gray.50" } : undefined}
                    onClick={expandable ? () => toggleRow(key) : undefined}
                  >
                    <Td py={4} px={4} fontSize="14px" color="gray.700" textAlign="left">
                      <HStack spacing={2}>
                        {expandable && (
                          <IconButton
                            size="xs"
                            variant="ghost"
                            aria-label={isOpen ? "Collapse" : "Expand"}
                            icon={isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                            onClick={(e) => { e.stopPropagation(); toggleRow(key); }}
                          />
                        )}
                        <Box w="8px" h="8px" bg={accent} borderRadius="sm" />
                        <Text fontWeight="500">{r.fuel}</Text>
                      </HStack>
                    </Td>
                    <Td py={4} px={4} fontSize="14px" color="gray.500" textAlign="left">{r.unit || "—"}</Td>
                    <Td py={4} px={4} fontSize="14px" color="gray.700" fontWeight="600" textAlign="right">{formatNumber(r.consumption, 0)}</Td>
                    <Td py={4} px={4} fontSize="14px" color="gray.700" fontWeight="600" textAlign="right">{r.kwh != null ? formatNumber(r.kwh, 0) : "—"}</Td>
                    <Td py={4} px={4} fontSize="14px" color={accent} fontWeight="700" textAlign="right">
                      {formatTco2(r.emissions)}
                      {pctChange != null && (
                        <Text fontSize="11px" color={pctChange < 0 ? "green.600" : "red.600"} fontWeight="600">
                          {pctChange < 0 ? "↓" : "↑"} {Math.abs(pctChange).toFixed(1)}% vs prev
                        </Text>
                      )}
                    </Td>
                    <Td py={4} px={4} textAlign="left" minW="220px">
                      <HStack spacing={3}>
                        <Box flex="1" h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                          <Box h="100%" w={`${share}%`} bg={accent} borderRadius="full" />
                        </Box>
                        <Text fontSize="13px" color="gray.700" fontWeight="600" minW="48px" textAlign="right">
                          {share.toFixed(1)}%
                        </Text>
                      </HStack>
                    </Td>
                  </Tr>
                  {isOpen && (
                    <>
                      {/* Sub-header anchored to the parent's columns so the
                          dimension rows line up with Consumption / kWh /
                          Emissions in the main table. */}
                      <Tr bg="gray.50">
                        <Td py={2} pl={12} pr={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="700">
                          {levelName || "Dimension"}
                        </Td>
                        <Td py={2} px={4} />
                        <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="700" textAlign="right">Consumption</Td>
                        <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="700" textAlign="right">kWh equivalent</Td>
                        <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="700" textAlign="right">Emissions (tCO₂e)</Td>
                        <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="700" textAlign="left">{`Share of ${r.fuel}`}</Td>
                      </Tr>
                      {dimSplit.length === 0 ? (
                        <Tr bg="gray.50">
                          <Td colSpan={6} py={3} pl={12} fontSize="13px" color="gray.400">
                            {`No ${(levelName || "dimension").toLowerCase()} recorded this fuel`}
                          </Td>
                        </Tr>
                      ) : dimSplit.map((d) => (
                        <Tr key={d.id} bg="gray.50">
                          <Td py={2.5} pl={12} pr={4} fontSize="13px" color="gray.600">
                            <HStack spacing={2}>
                              <Box w="6px" h="6px" bg={accent} opacity={0.6} borderRadius="sm" />
                              <Text>{d.name}</Text>
                            </HStack>
                          </Td>
                          <Td py={2.5} px={4} fontSize="13px" color="gray.400" textAlign="left">{r.unit || "—"}</Td>
                          <Td py={2.5} px={4} fontSize="13px" color="gray.600" fontWeight="600" textAlign="right">{formatNumber(d.consumption, 0)}</Td>
                          <Td py={2.5} px={4} fontSize="13px" color="gray.600" fontWeight="600" textAlign="right">{d.kwh != null ? formatNumber(d.kwh, 0) : "—"}</Td>
                          <Td py={2.5} px={4} fontSize="13px" color={accent} fontWeight="700" textAlign="right">{formatTco2(d.emissions)}</Td>
                          <Td py={2.5} px={4} textAlign="left" minW="220px">
                            <HStack spacing={3}>
                              <Box flex="1" h="5px" bg="gray.200" borderRadius="full" overflow="hidden">
                                <Box
                                  h="100%"
                                  w={`${dimSplitTotal > 0 ? (d.emissions / dimSplitTotal) * 100 : 0}%`}
                                  bg={accent}
                                  opacity={0.7}
                                  borderRadius="full"
                                />
                              </Box>
                              <Text fontSize="12px" color="gray.600" fontWeight="600" minW="48px" textAlign="right">
                                {dimSplitTotal > 0 ? ((d.emissions / dimSplitTotal) * 100).toFixed(1) : "0.0"}%
                              </Text>
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </>
                  )}
                </Fragment>
              );
            })}
            {sorted.length > 0 && (
              <Tr bg="gray.50" borderTop="2px solid" borderColor="gray.200">
                <Td py={4} px={4} fontSize="13px" color="darkTeal" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" textAlign="left">Total</Td>
                <Td py={4} px={4} />
                {/* Consumption mixes units across fuels, so it's not summed. */}
                <Td py={4} px={4} fontSize="14px" color="gray.400" fontWeight="700" textAlign="right">—</Td>
                {/* kWh-equivalent is unit-normalised, so it can be totalled. */}
                <Td py={4} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{totalKwh != null ? formatNumber(totalKwh, 0) : "—"}</Td>
                <Td py={4} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{formatTco2(totalEmissions)}</Td>
                <Td py={4} px={4} textAlign="left" minW="220px">
                  <Text fontSize="13px" color="gray.700" fontWeight="700" textAlign="right">100%</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
      <Text fontSize="11px" color="gray.500" mt={2}>
        The same fuel measured in different units (e.g. LNG in Tonnes vs kWh) stays as separate rows — combining them would erase the source-data distinction. Raw consumption isn&apos;t totalled because it mixes units; the kWh-equivalent is unit-normalised, so its total is shown.
      </Text>
    </Box>
  );
};

// Flat-table variant kept around for ScopeTwoDetail's import path. It now
// only renders a single category's consumption + emissions, since the new
// Scope 1 layout owns the fuel breakdown logic above.
export const ConsumptionTable = ({ title, data }) => {
  const [view, setView] = useState("summary");
  const hasData = Array.isArray(data) && data.length > 0;

  const totalConsumption = hasData
    ? data.reduce((acc, it) => acc + (Number(it.consumption) || 0), 0)
    : 0;
  const totalEmissions = hasData
    ? data.reduce((acc, it) => acc + (Number(it.emissions) || 0), 0)
    : 0;
  const units = hasData
    ? Array.from(new Set(data.map((it) => it.measurementUnit).filter(Boolean)))
    : [];
  const sharedUnit = units.length === 1 ? units[0] : null;

  return (
    <Box>
      <Flex align="center" justify="space-between" mb={3} gap={2}>
        <Text fontSize="body.m" fontWeight="600" color="gray.700">
          {title}
        </Text>
        <ConsumptionViewToggle selected={view} setSelected={setView} />
      </Flex>

      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
      {view === "summary" ? (
        hasData ? (
          <Table size="sm" variant="unstyled">
            <Thead>
              <Tr bg="gray.50">
                <Th px={4} py={2} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600">
                  Type
                </Th>
                <Th px={4} py={2} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" isNumeric>
                  Consumption
                </Th>
                <Th px={4} py={2} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" isNumeric>
                  Emissions (tCO2e)
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.map((item, idx) => (
                <Tr key={idx} borderTop={idx === 0 ? "none" : "1px solid"} borderColor="gray.100">
                  <Td px={4} py={2.5} fontSize="body.s" color="gray.700">
                    <Text as="span" fontWeight="600">{prettyLabel(item.consumptionType)}</Text>
                    {item.measurementUnit && (
                      <Text as="span" color="gray.400" ml={2} fontSize="body.xs">
                        {item.measurementUnit}
                      </Text>
                    )}
                  </Td>
                  <Td px={4} py={2.5} fontSize="body.s" color="gray.700" isNumeric>
                    {getFormattedNumValue(item.consumption, 2) || 0}
                  </Td>
                  <Td px={4} py={2.5} fontSize="body.s" color="gray.700" isNumeric>
                    {getFormattedNumValue(item.emissions, 2) || 0}
                  </Td>
                </Tr>
              ))}
              <Tr bg="lightLime" borderTop="2px solid" borderColor="gray.200">
                <Td px={4} py={2.5} fontSize="body.s" fontWeight="700" color="darkTeal">Total</Td>
                <Td px={4} py={2.5} fontSize="body.s" fontWeight="700" color="darkTeal" isNumeric>
                  {getFormattedNumValue(totalConsumption, 2) || 0}
                  {sharedUnit && (
                    <Text as="span" color="gray.500" fontWeight="500" ml={1} fontSize="body.xs">{sharedUnit}</Text>
                  )}
                </Td>
                <Td px={4} py={2.5} fontSize="body.s" fontWeight="700" color="darkTeal" isNumeric>
                  {getFormattedNumValue(totalEmissions, 2) || 0}
                </Td>
              </Tr>
            </Tbody>
          </Table>
        ) : (
          <Flex align="center" justify="center" py={8}>
            <Text color="gray.400" fontSize="body.s">No data</Text>
          </Flex>
        )
      ) : (
        <Flex align="center" justify="center" py={12} direction="column" gap={1}>
          <Text fontSize="body.s" color="gray.500" fontWeight="600">Visual view</Text>
          <Text fontSize="body.xs" color="gray.400">Coming soon</Text>
        </Flex>
      )}
      </Box>
    </Box>
  );
};

const ConsumptionViewToggle = ({ selected, setSelected }) => {
  const options = [
    { label: "Summary", value: "summary", icon: <ListBullets size={14} /> },
    { label: "Visual", value: "visual", icon: <ChartDonut size={14} /> },
  ];
  return (
    <Flex bg="gray.100" borderRadius="full" p="3px">
      {options.map((option) => {
        const active = selected === option.value;
        return (
          <Button
            key={option.value}
            size="xs"
            borderRadius="full"
            bg={active ? "pureBlack" : "transparent"}
            color={active ? "white" : "gray.500"}
            fontWeight={active ? "600" : "normal"}
            rightIcon={option.icon}
            onClick={() => setSelected(option.value)}
            _hover={{ bg: active ? "pureBlack" : "gray.200" }}
            px={3}
          >
            {option.label}
          </Button>
        );
      })}
    </Flex>
  );
};
export default ScopeOneDetail;
